import React, { useEffect, useRef, useState } from 'react';
import {
  boardLayout, cellAt, cellsBetween, drawCells, drawClueAreas, drawGridLines, drawHover, prepareCanvas,
} from './nonoDraw';

// Paints a drawing. Left button toggles from the first cell's value, right button erases,
// the Erase tool does the same without a right button, Shift keeps the stroke on one row or column. Mirroring paints the mirrored cells too. With a line tool
// ('row' or 'col') a click deletes the row or column under the pointer instead.
function NonoGridEditor({ width, height, cells, analysis, showErrors, mirror, lineTool, erase, readOnly, maxWidth, onStrokeStart, onChange, onDeleteLine }) {
  const canvasRef = useRef(null);
  const [hover, setHover] = useState(null);
  const stroke = useRef(null);
  const layout = boardLayout(width, height, maxWidth, 640);

  useEffect(() => {
    const ctx = prepareCanvas(canvasRef.current, layout);
    drawClueAreas(ctx, layout, analysis?.clues, hover);
    drawCells(ctx, layout, cells, { unresolved: showErrors && analysis?.status !== 'empty' ? analysis?.unresolvedCells : null });
    drawGridLines(ctx, layout);
    drawHover(ctx, layout, hover);
    if (lineTool && hover) {
      ctx.fillStyle = 'rgba(224, 49, 49, 0.3)';
      if (lineTool === 'row') ctx.fillRect(layout.left, layout.top + hover.r * layout.cell, width * layout.cell, layout.cell);
      else ctx.fillRect(layout.left + hover.c * layout.cell, layout.top, layout.cell, height * layout.cell);
    }
  });

  const paintCells = (base, targets, value) => {
    const next = [...base];
    const set = (r, c) => { next[r * width + c] = value; };
    targets.forEach(({ r, c }) => {
      set(r, c);
      if (mirror.x) set(r, width - 1 - c);
      if (mirror.y) set(height - 1 - r, c);
      if (mirror.x && mirror.y) set(height - 1 - r, width - 1 - c);
    });
    return next;
  };

  const point = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const onPointerDown = (e) => {
    if (readOnly) return;
    const { x, y } = point(e);
    const cell = cellAt(layout, x, y);
    if (!cell) return;
    e.preventDefault();
    if (lineTool) {
      onDeleteLine(lineTool, lineTool === 'row' ? cell.r : cell.c);
      return;
    }
    canvasRef.current.setPointerCapture(e.pointerId);
    const value = e.button === 2 || erase ? 0 : cells[cell.r * width + cell.c] ? 0 : 1;
    const painted = paintCells(cells, [cell], value);
    stroke.current = { start: cell, last: cell, value, base: cells, cells: painted, line: e.shiftKey };
    onStrokeStart();
    onChange(painted);
  };

  const onPointerMove = (e) => {
    const { x, y } = point(e);
    const s = stroke.current;
    const cell = cellAt(layout, x, y, !!s);
    setHover(prev => (prev?.r === cell?.r && prev?.c === cell?.c ? prev : cell));
    if (!s || !cell || (cell.r === s.last.r && cell.c === s.last.c)) return;
    if (s.line) {
      const horizontal = Math.abs(cell.c - s.start.c) >= Math.abs(cell.r - s.start.r);
      const end = horizontal ? { r: s.start.r, c: cell.c } : { r: cell.r, c: s.start.c };
      s.cells = paintCells(s.base, cellsBetween(s.start, end), s.value);
    } else {
      s.cells = paintCells(s.cells, cellsBetween(s.last, cell), s.value);
    }
    onChange(s.cells);
    s.last = cell;
  };

  const onPointerUp = () => { stroke.current = null; };

  return (
    <canvas
      ref={canvasRef}
      className="nn-canvas"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      onPointerLeave={() => !stroke.current && setHover(null)}
      onContextMenu={e => e.preventDefault()}
    />
  );
}

export default NonoGridEditor;
